import { ReactElement, useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { dequal } from 'dequal';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCloudSync } from '@components/icons/cloud-sync';
import { IconCloudUnsync } from '@components/icons/cloud-unsync';
import { IconDate } from '@components/icons/date';
import { IconEdit } from '@components/icons/edit';
import { IconIntegration } from '@components/icons/integration';
import { IconList } from '@components/icons/list';
import { IconRestart } from '@components/icons/restart';
import { IconUrl } from '@components/icons/url';
import { List } from '@components/integrations/integrations-types';
import {
  CloseButton,
  forceHideSidebar,
  Sidebar,
  useSidebar,
  useSidebarEventHandlers,
} from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { uuid } from '@utils/uuid';

import {
  useCreateList,
  useEditList,
  useList,
  usePatchSyncList,
  useSyncList,
} from 'api/lists/hooks';
import { useUnsavedChangesPromptModal } from 'ui/journey-builder/unsaved-changes-prompt';

import { useSyncListSidebar } from './sync-list-sidebar';

import { DraggableOption, OptionCard } from '../draggable-option';

export const useListSidebar = () => {
  const sidebar = useSidebar(ListEditSidebar, {
    variant: 'CONTENT_HEAVY',
    closeOnClickOutside: true,
  });
  return sidebar;
};

const Form = createForm<Omit<List, 'shortId' | 'options'>>();

export type DraggingState = {
  id: number;
  order: number;
  index: number;
} | null;

export type Option = {
  value: string;
  label: string;
  shortId: string;
};

type SelectSidebarProps = {
  listId: string;
};

const ListEditSidebar = (props: SelectSidebarProps) => {
  const { listId } = props;
  const { t } = useScopedTranslate(SelectSidebarLocale);
  const { setOnBeforeChange } = useSidebarEventHandlers();
  const showSyncListSidebar = useSyncListSidebar();
  const formStore = useCreateFormStore();
  const editSyncListMutation = usePatchSyncList();
  const reSyncListMutation = useSyncList();
  const listQuery = useList(listId ?? '', {
    onSuccess: (list) => {
      setOptions(list?.options ?? []);
    },
    staleTime: Infinity,
    cacheTime: 0,
  });

  const list = listQuery.data;

  const [options, setOptions] = useState<Array<Option>>(list?.options ?? []);
  const [noOptionsError, setNoOptionsError] = useState<boolean>(false);
  const [newOptionUnsavedError, setNewOptionUnsavedError] = useState<boolean>(false);
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const [displayMissingIds, setDisplayMissingIds] = useState<string[]>();
  const [newOptionValue, setNewOptionValue] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEdit = Boolean(list);
  const isOptionsEmpty = options.length === 0;

  const draggingOption = draggingItem ? options[draggingItem.index] : null;
  const optionIds = options.map((option) => String(option.value));

  const listSyncData = list?.sync;
  const isAddOptionVisable = newOptionValue !== null || !listSyncData;

  const create = useCreateList({
    onSuccess: forceHideSidebar,
    onError: (error) =>
      setErrors({ name: error.response?.data.nonFieldErrors?.[0] ?? t('unknownError') }),
  });
  const edit = useEditList({
    onSuccess: forceHideSidebar,
    onError: (error) =>
      setErrors({ name: error.response?.data.nonFieldErrors?.[0] ?? t('unknownError') }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const order = active.data.current?.order;
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: active.data.current?.id,
    });
  };

  const onDragEnd = async (event: DragEndEvent) => {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;
    const overIndex = optionIds.indexOf(String(over.id));
    const activeIndex = optionIds.indexOf(String(active.id));
    changeOptionOrder(activeIndex, overIndex);
  };

  const onDragCancel = () => {
    setDraggingItem(null);
  };

  const editOption = (index: number, option: Option) => {
    const wasNew = newOptionValue === option.value;
    const newOptions = [...options];
    newOptions[index] = option;
    if (displayMissingIds?.includes(newOptions[index].value)) {
      const newMissingIds = displayMissingIds.filter((id) => id !== newOptions[index].value);
      setDisplayMissingIds(newMissingIds);
    }
    setNewOptionValue(null);
    setOptions(newOptions);
    if (wasNew) {
      handleAddOption();
    }
  };

  const removeOption = (index: number, isCancel?: boolean) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    if (displayMissingIds?.includes(newOptions[index].value)) {
      const newMissingIds = displayMissingIds.filter((id) => id !== newOptions[index].value);
      setDisplayMissingIds(newMissingIds);
    }
    isCancel && setNewOptionValue(null);
    setOptions(newOptions);
  };

  const changeOptionOrder = (activeIndex: number, overIndex: number) => {
    const newOptions = [...options];
    const [removed] = newOptions.splice(activeIndex, 1);
    newOptions.splice(overIndex, 0, removed);
    setOptions(newOptions);
  };

  const handleAddOption = () => {
    const newOption = {
      value: '',
      label: '',
      shortId: uuid(),
    };
    const newOptions = [...options];
    newOptions.push(newOption);
    setNewOptionValue(newOption.value);
    setOptions(newOptions);
  };

  const isOptionsDirty = () => {
    const isEmptyOptions = isOptionsEmpty && !isEdit;
    return !isEmptyOptions && !dequal(options, list?.options);
  };

  const isDuplicateValue = (value: string, shortId: string) => {
    return options.some((option) => {
      return option.value === value && option.shortId !== shortId;
    });
  };

  const isUuid = (value: unknown) => {
    return (
      typeof value === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    );
  };
  const handleSave = async (data: { name: string; source: string }) => {
    if (!formStore.actions.validateForm()) {
      return false;
    }
    if (isOptionsEmpty && !listSyncData) {
      setNoOptionsError(true);
      return false;
    }
    if (newOptionValue !== null) {
      setNewOptionUnsavedError(true);
      return false;
    }

    const newOptions = options.map((option) => {
      const { shortId, ...rest } = option;
      return isUuid(shortId) ? rest : option;
    });

    if (list) {
      await edit.mutateAsync({ ...data, id: list.shortId, options: newOptions });
    } else {
      await create.mutateAsync({ ...data, options: newOptions });
    }
    return true;
  };

  const showModal = useUnsavedChangesPromptModal(
    () => handleSave(formStore.getState().values as List),
    SelectSidebarLocale
  );
  const isDirty = formStore.useIsDirty() || isOptionsDirty();

  useEffect(() => {
    if (isDirty) {
      setOnBeforeChange(() => {
        showModal();
        return false;
      });
    } else {
      setOnBeforeChange(undefined);
    }
    return () => setOnBeforeChange(undefined);
  }, [isDirty, setOnBeforeChange, showModal]);

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            <IconList color="elementBlue100" />
            <Sidebar.HeaderTitle title={isEdit ? t('titleEdit') : t('title')} />
          </Stack>
          <Stack space={2} orientation="horizontal" align="center">
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={6}>
            <Container>
              <Text text={t('details')} font="semibold14" />
              <Stack space={4}>
                <Form store={formStore} updateErrors={errors} onSubmit={handleSave}>
                  <Form.TextField
                    id="name"
                    label={t('name')}
                    defaultValue={list?.name}
                    validators={['required']}
                    maxChars={255}
                  />
                  <Form.TextField id="source" label={t('source')} defaultValue={list?.source} />
                  {listSyncData && (
                    <Card variant="transparent" space={4}>
                      <Stack space={4}>
                        <CardItem
                          title={t('sourceIntegration')}
                          text={listSyncData.integration.name}
                          icon={
                            listSyncData.integration.thumbnail ? (
                              <SquareImage src={listSyncData.integration.thumbnail} />
                            ) : (
                              <IconIntegration />
                            )
                          }
                        />
                        <CardItem
                          title={t('url')}
                          text={listSyncData.endpointUrl}
                          icon={<IconUrl />}
                        />
                        <Stack
                          space={4}
                          orientation="horizontal"
                          align="center"
                          justify="space-between"
                        >
                          <Stack space={4} orientation="horizontal" align="center">
                            <IconFancyContainer icon={<IconCloudSync />} variant="purple" />
                            <div>
                              <Text text={t('autoSync')} color="textSecondary" font="semibold14" />
                              <Text
                                text={styledTranslate({
                                  key: 'autoSyncHours',
                                  variables: {
                                    status: {
                                      text: listSyncData.isActive ? t('enabled') : t('disabled'),
                                      color: 'textPrimary',
                                      font: 'regular14',
                                    },
                                  },
                                  translations: SelectSidebarLocale,
                                })}
                                font="regular14"
                              />
                            </div>
                          </Stack>
                          <Switch
                            label={t('autoSync')}
                            labelVisible={false}
                            defaultValue={listSyncData.isActive}
                            onChange={(isActive) => {
                              editSyncListMutation.mutateAsync({
                                id: listSyncData.shortId,
                                data: { isActive },
                              });
                            }}
                          />
                        </Stack>
                        <CardItem
                          title={t('lastSync')}
                          text={abbreviateDate(listSyncData.lastSync, 'en', 'datetime')}
                          icon={<IconDate />}
                        />
                        {listSyncData.hasError && (
                          <CardItem
                            title={t('error')}
                            text={t('syncFailed', { error_message: listSyncData.errorMessage })}
                            icon={<IconCloudUnsync />}
                            isError
                          />
                        )}
                      </Stack>
                    </Card>
                  )}
                  <Form.Actions portal="list-form-actions">
                    <Sidebar.Footer>
                      <Stack
                        space={2}
                        orientation="horizontal"
                        justify={listSyncData ? 'space-between' : 'flex-end'}
                      >
                        <Stack space={2} orientation="horizontal" align="center">
                          {list && listSyncData && (
                            <Button
                              variant="secondary"
                              text={t('core.edit')}
                              onClick={() =>
                                showSyncListSidebar({
                                  list: list,
                                })
                              }
                              iconLeft={<IconEdit />}
                            />
                          )}
                          {listSyncData && (
                            <Button
                              variant="secondary"
                              text={t('resync')}
                              onClick={() => {
                                reSyncListMutation.mutateAsync(listSyncData.shortId);
                              }}
                              iconLeft={<IconRestart />}
                            />
                          )}
                        </Stack>
                        <Stack space={4} orientation="horizontal" justify="end">
                          <Button
                            variant="secondary"
                            text={t('core.cancel')}
                            onClick={() => {
                              forceHideSidebar();
                            }}
                          />
                          <Form.Submit
                            variant="primary-blue"
                            type="submit"
                            text={list ? t('core.save') : t('title')}
                            isDisabled={newOptionValue !== null || !isDirty}
                          />
                        </Stack>
                      </Stack>
                    </Sidebar.Footer>
                  </Form.Actions>
                </Form>
              </Stack>
            </Container>
            <Sidebar.Separator />
            <Container>
              <Text text={t('options')} font="semibold14" />
              <Stack space={4}>
                <Stack space={1}>
                  <Stack space={2}>
                    {options.length === 0 ? (
                      <Stack space={1}>
                        <Card variant="gray" space={4}>
                          <Stack space={4} orientation="horizontal" align="center">
                            <IconList color="elementPurple60" />
                            <Text text={t('noOptions')} color="textSecondary" font="semibold14" />
                          </Stack>
                        </Card>
                        {noOptionsError && <FormError text={t('optionsError')} />}
                      </Stack>
                    ) : listSyncData ? (
                      <>
                        {options.map((option) => (
                          <Stack space={2} key={option.shortId}>
                            <Card variant="transparent" spaceX={4} spaceY={2}>
                              <Text
                                text={option.label}
                                font="regular14"
                                style={{ wordBreak: 'break-word' }}
                              />
                              <Text
                                text={option.value}
                                font="regular14"
                                style={{ wordBreak: 'break-word' }}
                              />
                            </Card>
                          </Stack>
                        ))}
                      </>
                    ) : (
                      <>
                        <DndContext
                          sensors={sensors}
                          onDragStart={onDragStart}
                          onDragEnd={onDragEnd}
                          onDragCancel={onDragCancel}
                        >
                          <DragOverlay>
                            {draggingOption && (
                              <OptionCard
                                option={draggingOption}
                                index={Number(draggingItem?.index)}
                              />
                            )}
                          </DragOverlay>
                          <SortableContext items={optionIds} strategy={verticalListSortingStrategy}>
                            {options.map((option, index) => (
                              <Stack space={2} key={option.shortId}>
                                <DraggableOption
                                  option={option}
                                  index={index}
                                  editOption={editOption}
                                  removeOption={(isCancel) => removeOption(index, isCancel)}
                                  isDragging={draggingOption?.value === option.value}
                                  newOptionValue={newOptionValue}
                                  resetCustomError={setNewOptionUnsavedError}
                                  displayMissing={
                                    displayMissingIds
                                      ? displayMissingIds.includes(option.value)
                                      : false
                                  }
                                  isDuplicateValue={isDuplicateValue}
                                  setOptions={setOptions}
                                />
                              </Stack>
                            ))}
                          </SortableContext>
                        </DndContext>
                        {newOptionUnsavedError && <FormError text={t('newOptionUnsavedError')} />}
                      </>
                    )}
                    {isAddOptionVisable && (
                      <Stack align="end">
                        <Button
                          variant="secondary"
                          iconLeft={<IconAddPlus />}
                          text={t('addOption')}
                          onClick={() => handleAddOption()}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Container>
          </Stack>
          <div id="list-form-actions" />
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

type CardItemProps = {
  title: string;
  text: string;
  icon: ReactElement<IconProps>;
  isError?: boolean;
};

const CardItem = (props: CardItemProps) => {
  const { title, text, icon, isError } = props;
  return (
    <Stack space={4} orientation="horizontal">
      <IconFancyContainer icon={icon} variant={isError ? 'red' : 'purple'} />
      <div>
        <Text text={title} font="semibold14" color={isError ? 'elementRed200' : 'textSecondary'} />
        <Text text={text} font="regular14" color={isError ? 'elementRed200' : 'textSecondary'} />
      </div>
    </Stack>
  );
};

const Container = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  '>div:nth-child(2)': {
    width: pxToRem(424),
  },
});

const SelectSidebarLocale = {
  en: {
    details: 'Details',
    name: 'List name',
    source: 'Source',
    title: 'Add list',
    titleEdit: 'Edit list',
    options: 'Options',
    addOption: 'Add option',
    noOptions: 'No options added',
    optionsError: 'Please add options',
    newOptionUnsavedError: 'Please either cancel or add the new option',
    back: 'Back',
    add: 'Add',
    update: 'Update',
    unknownError: 'Unknown error',
    saveChangesTitle: 'Save changes?',
    hasUnsaved: 'This list has unsaved changes.\nWould you like to save or discard them?',
    saveChanges: 'Save list',
    sourceIntegration: 'Source integration',
    url: 'URL',
    autoSync: 'Automatic sync',
    lastSync: 'Last sync',
    autoSyncHours: '{status} • Every 24 hours 00:00 (GMT)',
    enabled: 'Enabled',
    disabled: 'Disabled',
    resync: 'Re-sync list',
    error: 'Error',
    syncFailed: 'Last sync failed • {error_message}',
  },
};
